import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import useUIPreferenceStore from '../../stores/useUIPreferenceStore';
import AgentAvatar from '../pages/AgentAvatar';
import useContextAgentLauncherAttention, {
  ContextAgentLauncherBubble,
} from './useContextAgentLauncherAttention';
import { useContextAgent } from './ContextAgentContext';
import useFloatingDrag from './useFloatingDrag';
import { CONTEXT_AGENT_SPRITE_SIZE } from './widgetConstants';
import './ContextAgentWidgetLauncher.css';

const ENTER_ANIMATION = 'context-agent-widget-enter';

/** Right-edge launcher — draggable agent avatar or pixel sprite. */
export default function ContextAgentWidgetLauncher() {
  const assistantLayout = useUIPreferenceStore((s) => s.assistantLayout);
  const agentDisplayStyle = useUIPreferenceStore((s) => s.agentDisplayStyle);
  const { registration, open, setOpen, toggleOpen, isRegistered, widgetAnchor, setWidgetAnchor } =
    useContextAgent();

  const [playEnterAnimation, setPlayEnterAnimation] = useState(true);
  const prevScopeKeyRef = useRef(null);

  const {
    scopeKey,
    attentionMessage,
    bubbleVisible,
    bubbleExiting,
    handleBubbleClick,
    handleBubbleDismiss,
    handleBubbleAnimationEnd,
  } = useContextAgentLauncherAttention(registration, { open, setOpen });

  useEffect(() => {
    if (scopeKey == null || prevScopeKeyRef.current === scopeKey) {
      return;
    }
    prevScopeKeyRef.current = scopeKey;
    setPlayEnterAnimation(true);
  }, [scopeKey]);

  const handleSpriteMove = useCallback(
    (next) => {
      setWidgetAnchor(next);
    },
    [setWidgetAnchor]
  );

  const {
    targetRef: wrapRef,
    isDragging,
    handlePointerDown,
    handlePointerMove,
    finishPointerInteraction,
  } = useFloatingDrag({
    onMove: handleSpriteMove,
    disabled: !isRegistered || assistantLayout !== 'widget' || open,
  });

  const handlePointerUp = useCallback(
    (event) => {
      const wasDrag = finishPointerInteraction(event);
      if (wasDrag) {
        setPlayEnterAnimation(false);
      } else {
        toggleOpen();
      }
    },
    [finishPointerInteraction, toggleOpen]
  );

  const handleEnterAnimationEnd = useCallback((event) => {
    if (event.currentTarget !== event.target || event.animationName !== ENTER_ANIMATION) {
      return;
    }
    setPlayEnterAnimation(false);
  }, []);

  const agentName = registration?.agentName;
  const scopeLabel = registration?.scopeLabel;

  if (assistantLayout !== 'widget' || !isRegistered || !registration || open) {
    return null;
  }
  const tooltip = scopeLabel
    ? `${agentName} · ${scopeLabel} · Drag to move`
    : `${agentName} · Drag to move`;

  const wrapClassName = [
    'context-agent-widget-launcher-wrap',
    playEnterAnimation ? 'context-agent-widget-launcher-wrap--enter' : '',
    isDragging ? 'is-dragging' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tooltip
      title={tooltip}
      placement="left"
      arrow
      disableInteractive
      disableHoverListener={bubbleVisible}
    >
      <div
        ref={wrapRef}
        key={scopeKey}
        className={wrapClassName}
        style={{
          left: widgetAnchor.x,
          top: widgetAnchor.y,
          '--context-agent-sprite-size': `${CONTEXT_AGENT_SPRITE_SIZE}px`,
        }}
        role="button"
        tabIndex={0}
        aria-label={`Open ${agentName}. Drag to reposition.`}
        aria-pressed={false}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onAnimationEnd={handleEnterAnimationEnd}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleOpen();
          }
        }}
        data-testid="context-agent-widget-launcher"
        data-agent-display-style={agentDisplayStyle}
      >
        <ContextAgentLauncherBubble
          bubbleVisible={bubbleVisible}
          bubbleExiting={bubbleExiting}
          attentionMessage={attentionMessage}
          placement="top-end"
          onBubbleClick={handleBubbleClick}
          onBubbleDismiss={handleBubbleDismiss}
          onBubbleAnimationEnd={handleBubbleAnimationEnd}
        />
        <AgentAvatar
          agentId={registration?.launcherAgentId}
          agentName={agentName}
          name={agentName}
          size={CONTEXT_AGENT_SPRITE_SIZE}
        />
      </div>
    </Tooltip>
  );
}
